import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

import { queryKey as chunksKey } from '@utils/aureus/queries/chunks'
import { ChunkNumber } from '@widgets/Chunk'
import { JSONWidgetType as TeaserStandardHybridJSONWidgetType } from '@widgets/TeaserStandardHybrid'
import { JSONWidgetType as TeaserToiJSONWidgetType } from '@widgets/TeaserToi'
import { ChunksRecommendationInfo } from '@components/AureusManager'

export type RecommendedChunkContent = {
  mainItem?: TeaserToiJSONWidgetType
  items: TeaserStandardHybridJSONWidgetType[]
}

export type UseRecommendedChunkContent = (
  chunkNumber: ChunkNumber,
) => RecommendedChunkContent

type GetRecommendedChunkContent = (
  queryClient: QueryClient,
  chunkNumber: ChunkNumber,
) => RecommendedChunkContent

const getRecommendedChunkContent: GetRecommendedChunkContent = (
  queryClient,
  chunkNumber,
) =>
  (
    queryClient.getQueryData<ChunksRecommendationInfo>([
      chunksKey,
    ]) as ChunksRecommendationInfo
  )?.[chunkNumber - 1] ?? { items: [] }

const useRecommendedChunkContent: UseRecommendedChunkContent = (
  chunkNumber,
) => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [recommendedChunkContent, setRecommendedChunkContent] =
    useState<RecommendedChunkContent>(() =>
      getRecommendedChunkContent(queryClient, chunkNumber),
    )

  const updateRecommendedChunkContent = useCallback(
    (args: any) => {
      if (
        ['invalidate', 'success'].includes(args?.action?.type) &&
        args.query.queryKey[0] === chunksKey
      ) {
        setRecommendedChunkContent(
          getRecommendedChunkContent(queryClient, chunkNumber),
        )
      }
    },
    [chunkNumber, queryClient],
  )

  useEffect(() => {
    setRecommendedChunkContent(
      getRecommendedChunkContent(queryClient, chunkNumber),
    )

    unsubscribeFnRef.current = queryCache.subscribe(
      updateRecommendedChunkContent,
    )

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [chunkNumber, queryCache, queryClient, updateRecommendedChunkContent])

  return recommendedChunkContent
}

export { getRecommendedChunkContent }

export default useRecommendedChunkContent
